/**
 * Web Push key handling.
 *
 * A push subscription is bound to the VAPID public key that created it, so the
 * key is generated once and frozen (devops decision #5). The failure this file
 * exists to prevent: Vite reads env from the repo root via envDir, and if that
 * is ever misconfigured the key is simply `undefined` at runtime with no build
 * error — every participant would silently receive nothing for five days.
 *
 * So the key is validated loudly, at the moment of subscribing, not assumed.
 */

const RAW_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY

/** base64url, unpadded, 87 chars for a P-256 public key (devops decision #5). */
const VAPID_PUBLIC_KEY_LENGTH = 87

export function vapidKeyProblem(key = RAW_KEY) {
  if (!key) {
    return 'VITE_VAPID_PUBLIC_KEY is not set. Vite must read the repo-root .env (envDir: ".." in vite.config.js).'
  }
  if (!/^[A-Za-z0-9_-]+$/.test(key)) {
    return 'VITE_VAPID_PUBLIC_KEY is not unpadded base64url.'
  }
  if (key.length !== VAPID_PUBLIC_KEY_LENGTH) {
    return `VITE_VAPID_PUBLIC_KEY is ${key.length} chars, expected ${VAPID_PUBLIC_KEY_LENGTH}.`
  }
  return null
}

/** Throws with a message that says exactly what to fix. Never returns undefined. */
export function getVapidPublicKey() {
  const problem = vapidKeyProblem()
  if (problem) throw new Error(problem)
  return RAW_KEY
}

/**
 * Make sure the server holds a live subscription for this device.
 *
 * Idempotent and safe to call on every launch, which is the point: backend
 * deletes a subscription on a 404/410 from the push service (decision #29), and
 * browsers rotate endpoints on their own. Without this, a participant whose
 * subscription lapsed would go quiet for the rest of the study with no error
 * anyone sees — which is the same silent failure mode as the missing install
 * step, just later.
 *
 * Re-subscription is done here rather than in the service worker's
 * pushsubscriptionchange handler because the VAPID key lives in the build's env
 * and sw.js is a static file that never sees it.
 *
 * @param {(sub: object) => Promise<unknown>} postSubscription
 * @returns {Promise<'granted'|'unsupported'|'not-granted'|'failed'>}
 */
/*
 * SINGLE-FLIGHT. Two callers now want this answer - App's launch effect and
 * Home's A3 gate - and before this they could both be inside
 * pushManager.subscribe() at once for the same registration. That is a real
 * race on a real device, not a tidiness point: the second call can reject or
 * hang, and the first evidence of it is a participant who cannot enrol.
 *
 * Shared only WHILE IN FLIGHT. Once it settles the next call starts fresh, so
 * this is deduplication and never a cached verdict - the gate must be able to
 * ask again and get a current answer.
 */
let inFlight = null

export function ensurePushSubscription(postSubscription) {
  if (inFlight) return inFlight
  inFlight = establishSubscription(postSubscription).finally(() => {
    inFlight = null
  })
  return inFlight
}

async function establishSubscription(postSubscription) {
  if (typeof navigator === 'undefined') return 'unsupported'
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported'
  // Never prompts. Permission is only ever requested from a user gesture, on
  // the onboarding screen built for it.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return 'not-granted'
  }
  if (vapidKeyProblem()) return 'failed'

  try {
    const registration =
      (await navigator.serviceWorker.getRegistration('/')) ??
      (await navigator.serviceWorker.register('/sw.js', { scope: '/' }))
    await navigator.serviceWorker.ready

    const existing = await registration.pushManager.getSubscription()
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(),
      }))

    await postSubscription(subscription.toJSON())
    return 'granted'
  } catch {
    /*
     * SWALLOWED HERE, NOT IGNORED. This function must never throw into a launch
     * effect or into Home's render - a broken push service would white-screen
     * the app for someone who only wanted to answer a beep.
     *
     * The old comment said "Email is the fallback transport and the next launch
     * tries again". Email is gone, so the second half is now the whole reason:
     * the caller gets 'failed' and DECIDES. Before day 0 that is a closed gate
     * (onboardingSteps.isPushEstablished); after it, the next launch retries.
     */
    return 'failed'
  }
}

/** PushManager.subscribe wants a Uint8Array, not the base64url string. */
export function applicationServerKey(key = getVapidPublicKey()) {
  const padded = key.padEnd(key.length + ((4 - (key.length % 4)) % 4), '=')
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}
